/**
 * outcomeService.ts
 *
 * Step 7 — Outcome handoff. Implements CLAUDE.md §G.4:
 *   - Performing employee → increment / promotion / both → contract
 *     addendum trigger
 *   - Under-performing → PIP form → hand off to PIP module (out of scope)
 *
 * HR-only. Login-based. No magic link surface.
 */

import {
  doc, getDoc, getDocs, query, where, collection,
  serverTimestamp, updateDoc, Timestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { OutcomeRecord, OutcomeType } from '../types/appeal';
import { getAppraisal } from './appraisalService';
import { omitUndefined } from './firestoreUtils';

const COL = 'outcomeRecords';

// ─── Errors ────────────────────────────────────────────────────────

export class OutcomeError extends Error {
  constructor(
    public code: 'not_found' | 'wrong_state' | 'invalid',
    message: string,
  ) {
    super(message);
    this.name = 'OutcomeError';
  }
}

// ─── Record outcome ────────────────────────────────────────────────

export interface RecordOutcomeInput {
  cycleId: string;
  employeeId: string;
  appraisalId: string;
  outcomeType: OutcomeType;
  processedBy: string;

  // Increment / promotion payload (optional based on outcomeType)
  incrementAmount?: number;
  incrementCurrency?: string;
  promotionToTitle?: string;
  promotionToGrade?: string;
  effectiveDate?: string;
  contractAddendumRef?: string;

  // PIP payload
  pipFormRef?: string;

  notes?: string;
}

export async function recordOutcome(input: RecordOutcomeInput): Promise<OutcomeRecord> {
  const appraisal = await getAppraisal(input.appraisalId);
  if (!appraisal) throw new OutcomeError('not_found', 'Appraisal not found.');
  if (!appraisal.managerSignature) {
    throw new OutcomeError(
      'wrong_state',
      'Cannot record outcome before manager has signed the appraisal.',
    );
  }

  // Validate payload coherence
  if ((input.outcomeType === 'increment' || input.outcomeType === 'increment_and_promotion')
      && (input.incrementAmount == null || input.incrementAmount <= 0)) {
    throw new OutcomeError('invalid', 'Increment amount required and must be positive.');
  }
  if ((input.outcomeType === 'promotion' || input.outcomeType === 'increment_and_promotion')
      && !input.promotionToTitle?.trim()) {
    throw new OutcomeError('invalid', 'Promotion title required.');
  }
  if (input.outcomeType === 'pip' && !input.pipFormRef?.trim()) {
    throw new OutcomeError('invalid', 'PIP form reference required.');
  }

  // Strip undefined-valued optional fields. PIP without increment,
  // increment without promotion, etc. would otherwise hit Firestore's
  // "Unsupported field value: undefined" error. ENGINEERING.md §4.
  const data = omitUndefined({
    cycleId:             input.cycleId,
    employeeId:          input.employeeId,
    appraisalId:         input.appraisalId,
    outcomeType:         input.outcomeType,
    incrementAmount:     input.incrementAmount,
    incrementCurrency:   input.incrementCurrency,
    promotionToTitle:    input.promotionToTitle,
    promotionToGrade:    input.promotionToGrade,
    effectiveDate:       input.effectiveDate,
    contractAddendumRef: input.contractAddendumRef,
    pipFormRef:          input.pipFormRef,
    processedBy:         input.processedBy,
    processedAt:         serverTimestamp() as unknown as Timestamp,
    notes:               input.notes,
  }) as Omit<OutcomeRecord, 'id'>;

  // Atomic two-write commit: outcome create + appraisal back-reference.
  const outcomeRef = doc(collection(db, COL));
  const batch = writeBatch(db);
  batch.set(outcomeRef, data);
  batch.set(
    doc(db, 'appraisalForms', input.appraisalId),
    { outcomeId: outcomeRef.id },
    { merge: true },
  );
  await batch.commit();

  return { ...data, id: outcomeRef.id, processedAt: Timestamp.fromDate(new Date()) };
}

// ─── Read ──────────────────────────────────────────────────────────

export async function getOutcomeByAppraisal(appraisalId: string): Promise<OutcomeRecord | null> {
  const q = query(collection(db, COL), where('appraisalId', '==', appraisalId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Omit<OutcomeRecord, 'id'>) };
}

export async function listOutcomesForCycle(cycleId: string): Promise<OutcomeRecord[]> {
  const q = query(collection(db, COL), where('cycleId', '==', cycleId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<OutcomeRecord, 'id'>) }));
}

// ─── Close cycle ───────────────────────────────────────────────────

/**
 * Final cycle close. Should only be called when every appraisal has an
 * outcome recorded. Marks ReviewCycle.status='closed'.
 */
export async function closeCycle(cycleId: string): Promise<void> {
  const cycleRef = doc(db, 'reviewCycles', cycleId);
  const cycleSnap = await getDoc(cycleRef);
  if (!cycleSnap.exists()) throw new OutcomeError('not_found', 'Cycle not found.');

  const appraisalsQ = query(
    collection(db, 'appraisalForms'),
    where('cycleId', '==', cycleId),
  );
  const appraisalsSnap = await getDocs(appraisalsQ);
  const total = appraisalsSnap.size;
  let withOutcome = 0;
  for (const ad of appraisalsSnap.docs) {
    if ((ad.data() as { outcomeId?: string }).outcomeId) withOutcome += 1;
  }
  if (withOutcome < total) {
    throw new OutcomeError(
      'wrong_state',
      `Cannot close cycle: ${total - withOutcome} appraisal(s) missing outcome.`,
    );
  }

  await updateDoc(cycleRef, {
    status: 'closed',
    currentStep: 7,
    updatedAt: serverTimestamp(),
  });
}
